/* ==========================================================================
   OUTLETZONE — product-gallery.js
   Scroll-snap gallery with thumbnails, dot indicators and full-screen zoom.

   The slides are a plain horizontally-scrolling list, so swipe, momentum and
   keyboard scrolling all come from the browser. This component only syncs the
   indicators and provides the zoom surface.
   ========================================================================== */
(function () {
  'use strict';

  const { $, $$ } = window.OZ.helpers;

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slidesEl = this.querySelector('[data-gallery-slides]');
      this.slides = $$('[data-gallery-slide]', this);
      this.thumbs = $$('[data-gallery-thumb]', this);
      this.dots = $$('[data-gallery-dot]', this);
      if (!this.slidesEl || this.slides.length === 0) return;

      this.index = 0;

      this.observeSlides();
      this.bindThumbs();
      this.bindZoom();
      this.bindKeys();
    }

    /* Which slide is centred drives the indicators. An IntersectionObserver
       against the scroller avoids a scroll listener entirely. */
    observeSlides() {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const idx = this.slides.indexOf(entry.target);
            if (idx > -1) this.setActive(idx);
          });
        },
        { root: this.slidesEl, threshold: 0.6 }
      );
      this.slides.forEach((s) => io.observe(s));
    }

    setActive(index) {
      this.index = index;
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
      this.dots.forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
      const counter = this.querySelector('[data-gallery-counter]');
      if (counter) counter.textContent = `${index + 1} / ${this.slides.length}`;
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.slidesEl.scrollTo({
        left: slide.offsetLeft - this.slidesEl.offsetLeft,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    /* Called by <variant-selector> when the chosen variant has its own image. */
    goToMedia(mediaId) {
      const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (idx > -1) this.goTo(idx);
    }

    bindThumbs() {
      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.goTo(i);
        });
      });
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.goTo(i);
        });
      });
    }

    bindKeys() {
      this.slidesEl.setAttribute('tabindex', '0');
      this.slidesEl.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(Math.min(this.index + 1, this.slides.length - 1)); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(Math.max(this.index - 1, 0)); }
      });
    }

    /* Zoom builds its overlay lazily — the high-resolution source is never
       requested unless the customer actually asks to inspect the garment. */
    bindZoom() {
      const trigger = this.querySelector('[data-gallery-zoom]');
      if (!trigger) return;

      trigger.addEventListener('click', () => {
        const slide = this.slides[this.index];
        const src = slide?.dataset.zoomSrc;
        if (!src) return;

        let overlay = document.getElementById('GalleryZoom');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.id = 'GalleryZoom';
          overlay.className = 'zoom-overlay';
          overlay.setAttribute('role', 'dialog');
          overlay.setAttribute('aria-modal', 'true');
          overlay.innerHTML =
            '<button type="button" class="btn-icon zoom-overlay__close" data-zoom-close>' +
            '<svg class="icon" aria-hidden="true"><use href="#icon-close"></use></svg>' +
            '<span class="visually-hidden">Fermer</span></button><img alt="">';
          document.body.appendChild(overlay);

          overlay.addEventListener('click', (e) => {
            if (e.target.closest('[data-zoom-close]') || e.target.tagName === 'IMG') closeZoom();
          });
        }

        const img = overlay.querySelector('img');
        img.src = src;
        img.alt = slide.querySelector('img')?.alt || '';
        overlay.classList.add('is-open');
        document.body.classList.add('is-locked');
        this._releaseZoom = window.OZ.helpers.trapFocus(overlay, overlay.querySelector('[data-zoom-close]'));

        const closeZoom = () => {
          overlay.classList.remove('is-open');
          document.body.classList.remove('is-locked');
          if (this._releaseZoom) this._releaseZoom();
          trigger.focus();
          document.removeEventListener('keydown', onKey);
        };

        const onKey = (e) => { if (e.key === 'Escape') closeZoom(); };
        document.addEventListener('keydown', onKey);
      });
    }
  }

  customElements.define('product-gallery', ProductGallery);
})();
